import { useCallback, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  Decimation,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import "chartjs-adapter-date-fns";

import { useTimeNavigation } from "hooks/useTimeNavigation";
import { useDeviceChartCache } from "hooks/useDeviceChartCache";
import { ErrorContainer } from "styles/devices/deviceDetails.styles";
import { ChartsContainer } from "styles/devices/deviceCharts.styles";
import { TimeNavigation, ViewType } from "./charts/TimeNavigation";
import { ChartDataView } from "./charts/ChartDataView";
import { ChartsLoadingSpinner } from "./charts/ChartsLoadingSpinner";

ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  Decimation,
  zoomPlugin,
);

interface DeviceChartsProps {
  serialNumber: string;
}

export function DeviceCharts({ serialNumber }: DeviceChartsProps) {
  const {
    timeRange,
    navigationOffset,
    fromTimestamp,
    toTimestamp,
    displayedTimeRanges,
    periodLabel,
    handleTimeRangeChange,
    navigateBackward,
    navigateForward,
    returnToNow,
    handleCustomDateRange,
    getCurrentDates,
    isAtCurrentTime,
  } = useTimeNavigation();

  const [viewType, setViewType] = useState<ViewType>("combined");
  const [zoomKey, setZoomKey] = useState(0);
  const handleResetZoom = useCallback(() => setZoomKey((p) => p + 1), []);

  const {
    data,
    error,
    isLoading,
    isFetching,
    isExtendingCache,
    effectiveFromTimestamp,
    effectiveToTimestamp,
    handleViewportChange,
    handleReturnToNow,
    isAtCurrentTimeWithPan,
    isChartPanned,
    handleTimeRangeChangeWithReset,
  } = useDeviceChartCache({
    serialNumber,
    timeRangeValue: timeRange.value,
    fromTimestamp,
    toTimestamp,
    handleTimeRangeChange,
    returnToNow,
    isAtCurrentTime,
    handleResetZoom,
  });

  const navigation = (
    <TimeNavigation
      timeRange={timeRange}
      timeRanges={displayedTimeRanges}
      navigationOffset={navigationOffset}
      viewType={viewType}
      periodLabel={periodLabel}
      onTimeRangeChange={handleTimeRangeChangeWithReset}
      onNavigateBackward={navigateBackward}
      onNavigateForward={navigateForward}
      onReturnToNow={handleReturnToNow}
      onViewTypeChange={setViewType}
      onCustomDateRange={handleCustomDateRange}
      getCurrentDates={getCurrentDates}
      isAtCurrentTime={isAtCurrentTimeWithPan}
      canReturnToNow={isChartPanned}
    />
  );

  if (error) {
    return (
      <ChartsContainer>
        {navigation}
        <ErrorContainer>
          <p>
            Error loading chart data:{" "}
            {error instanceof Error ? error.message : "Unknown error"}
          </p>
        </ErrorContainer>
      </ChartsContainer>
    );
  }

  return (
    <ChartsContainer>
      {navigation}
      {isLoading && !data ? (
        <ChartsLoadingSpinner viewType={viewType} />
      ) : (
        <div style={{ position: "relative" }}>
          {isFetching && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(0,0,0,0.3)",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "8px",
                pointerEvents: "none",
              }}
            >
              <span style={{ color: "#ffd200", fontSize: "0.9rem" }}>
                {isExtendingCache ? "Loading older history..." : "Loading..."}
              </span>
            </div>
          )}
          <ChartDataView
            data={data}
            timeRangeValue={timeRange.value}
            effectiveFromTimestamp={effectiveFromTimestamp}
            effectiveToTimestamp={effectiveToTimestamp}
            viewType={viewType}
            timeRangeLabel={timeRange.label}
            zoomKey={zoomKey}
            onResetZoom={handleResetZoom}
            onViewportChange={handleViewportChange}
          />
        </div>
      )}
    </ChartsContainer>
  );
}
